import amplitude from 'amplitude-js';
import branch from 'branch-sdk';
import * as Fathom from 'fathom-client';
import ReactFacebookPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';

import {
  AMPLITUDE_KEY,
  BRANCH_KEY,
  ENV,
  FACEBOOK_PIXEL_ID,
  FATHOM_SITE_ID,
  GOOGLE_ANALYTICS_KEY,
} from '@pray/shared/config';
import { isOnBrowser } from '@pray/shared/utils';
import { authenticate } from '@pray/shared/utils/auth';
import { initializeBrazeSdk } from '@pray/shared/utils/braze';
import { getCookieSettings, isCookieBannerAccepted, shouldDisableTracking } from '@pray/shared/utils/gdpr';
import storage from '@pray/shared/utils/storage';

import logger from './logger';

// gatsby-browser calls this in the onClientEntry function (called immediately when site loads)
const defaultInitOptions = {
  skipAmplitude: false,
  skipBranch: false,
  skipFacebookPixel: false,
  skipBraze: false,
  skipGoogleAnalytics: false,
  skipAuthentication: false,
  skipHotjar: false,
};

/**
 * @param {object} initOptions
 * @param {boolean} [initOptions.skipAmplitude]
 * @param {boolean} [initOptions.skipBranch]
 * @param {boolean} [initOptions.skipFacebookPixel]
 * @param {boolean} [initOptions.skipBraze]
 * @param {boolean} [initOptions.skipGoogleAnalytics]
 * @param {boolean} [initOptions.skipAuthentication]
 * @param {boolean} [initOptions.skipHotjar]
 * @param {string} [initOptions.appName] - The name of the app calling initialize ('www' or 'studio')
 * @param {string} [initOptions.facebookPixelId] - Optional Facebook Pixel ID to use (defaults to FACEBOOK_PIXEL_ID)
 */
const initialize = async (initOptions = defaultInitOptions) => {
  const deviceId = storage.getDeviceId();
  const user = storage.getUser();
  const isUserRegistered = storage.isUserRegistered();
  const userId = user && user.id && isUserRegistered ? user.id : null;
  const options = { ...defaultInitOptions, ...initOptions };

  const {
    skipAmplitude,
    skipBranch,
    skipFacebookPixel,
    skipBraze,
    skipGoogleAnalytics,
    skipAuthentication,
    skipHotjar,
    appName,
  } = options;

  // Initialize Fathom first - it's privacy-first and doesn't require consent
  _initializeFathom();

  // Check if tracking should be disabled due to DNT, GDPR, or user preferences
  const shouldDisableAllTracking = shouldDisableTracking();

  // If tracking should be disabled, skip all other tracking services
  if (shouldDisableAllTracking) {
    logger.info('Tracking disabled due to DNT flag, GDPR requirements, or user preferences');
    // Only run authentication if not skipped
    if (isOnBrowser() && !skipAuthentication) {
      await authenticate();
    }
    return;
  }

  if (!skipAmplitude) _initializeAmplitude(deviceId, userId);
  if (!skipBranch) _initializeBranch(deviceId);
  if (!skipFacebookPixel) initializeFacebookPixel(FACEBOOK_PIXEL_ID);
  // Not awaited. gatsby-browser awaits initialize() inside onClientEntry, so anything awaited here sits
  // in front of hydration — and Hotjar costs two dynamic imports before authenticate() even starts. It
  // is session recording; nothing rendered depends on it. Only reproduces in production, where ENV is
  // 'master' and the branch actually runs. _initializeHotjar swallows its own failures.
  if (!skipHotjar) _initializeHotjar(appName);

  // initialize braze only if the user is registered
  if (isUserRegistered) {
    if (!skipBraze) initializeBrazeSdk(userId);
  }

  const isInitializationDelayed = !isCookieBannerAccepted();
  if (!isInitializationDelayed) {
    if (!skipGoogleAnalytics) _initializeGoogleAnalytics();
  }

  if (isOnBrowser()) {
    if (!skipAuthentication) await authenticate();
  }
};

function _initializeAmplitude(deviceId, userId) {
  if (shouldDisableTracking()) return;

  // set up amplitude
  // https://amplitude.github.io/Amplitude-JavaScript/
  // https://developers.amplitude.com/docs/advanced-settings#setting-configuration-options-javascript
  // https://github.com/amplitude/Amplitude-JavaScript/blob/master/src/options.js#L14
  const amplitudeConfigOptions = {
    deviceId,
    disableCookies: true, // uses localstorage instead
    includeUtm: true,
    includeReferrer: true,
    includeGclid: true,
    logLevel: ENV === 'master' ? 'WARN' : 'INFO',
  };

  amplitude.getInstance().init(AMPLITUDE_KEY, userId, amplitudeConfigOptions, () => {
    logger.info('Amplitude initialized');
  });
}

/**
 * Initialize Facebook Pixel with a specific pixel ID
 * Can be called multiple times to switch between different pixels
 * @param {string} pixelId - The Facebook Pixel ID to initialize
 */
export function initializeFacebookPixel(pixelId) {
  // Do not initialize Facebook Pixel if tracking is disabled (includes cookie banner, DNT, GDPR checks)
  if (shouldDisableTracking()) {
    return false;
  }

  const { isFacebookOptOut } = getCookieSettings();
  if (isFacebookOptOut) {
    return false;
  }

  // https://developers.facebook.com/docs/facebook-pixel/pixel-with-ads/conversion-tracking#advanced_match
  const advancedMatching = {};

  const options = {
    autoConfig: true, // set pixel's autoConfig
    debug: false, // enable logs
  };

  ReactFacebookPixel.init(pixelId, advancedMatching, options);
  return true;
}

/**
 * Initialize Microsoft Clarity for a specific project.
 * Injects the Clarity tag once (safe across client-side navigation) and respects the same
 * tracking-consent gate (cookie banner / DNT / GDPR) as the Facebook pixel.
 * @param {string} projectId - The Microsoft Clarity project ID
 * @returns {boolean} whether Clarity is now (or was already) active
 */
export function initializeMicrosoftClarity(projectId) {
  if (!isOnBrowser() || !projectId) {
    return false;
  }

  if (shouldDisableTracking()) {
    return false;
  }

  // Already bootstrapped (e.g. navigating between landing pages) — don't re-inject the tag.
  if (window.clarity) {
    return true;
  }

  window.clarity =
    window.clarity ||
    // eslint-disable-next-line func-names, prefer-rest-params
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.clarity.ms/tag/${projectId}`;
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(tag, firstScript);

  return true;
}

/**
 * Initialize Branch Web SDK with Journeys Assist enabled.
 *
 * JOURNEYS ASSIST - Attribution Fix for Newsletter → App Install Flow:
 * ─────────────────────────────────────────────────────────────────────
 * Problem: When users click from newsletter (Branch Link A) → land on web page →
 * click "Download App" button, a NEW Branch link was created that "stole"
 * attribution from the original newsletter link (showing 0 installs).
 *
 * Solution: Journeys Assist preserves the original referring link through the
 * install flow. When a user clicks a Branch Journey banner/CTA, instead of
 * creating a new link, it passes through the original attribution data.
 *
 * Extended Journeys Assist keeps the referring link data for 7 days, ensuring
 * attribution is preserved even if the user returns later to download the app.
 *
 * @see https://help.branch.io/docs/journeys-assist
 * @see https://help.branch.io/developers-hub/docs/web-sdk-overview
 */
function _initializeBranch(deviceId) {
  // Branch SDK configuration options
  const branchOptions = {
    // Enable Branch Journeys (smart banners/interstitials)
    no_journeys: false,

    // JOURNEYS ASSIST: Preserve original referring link through install CTAs.
    // When enabled, Journey banners will use the original referring link instead
    // of creating a new one, ensuring proper attribution to newsletter campaigns.
    enableExtendedJourneysAssist: true,

    // Keep referring link data for 7 days (in milliseconds).
    // This covers users who may return later to complete the app download.
    extendedJourneysAssistExpiryTime: 604800000, // 7 days = 7 * 24 * 60 * 60 * 1000
  };

  // Check if user has opted out of Branch tracking (GDPR/cookie preferences)
  const { isBranchOptOut } = getCookieSettings();
  const isBranchTrackingDisabled = !isCookieBannerAccepted() || isBranchOptOut || shouldDisableTracking();

  // Disable tracking if user hasn't consented or has opted out
  // https://help.branch.io/developers-hub/docs/web-advanced-features#enable--disable-user-tracking
  if (isBranchTrackingDisabled) {
    branchOptions.tracking_disabled = true;
  }

  // Initialize Branch SDK with configured options
  branch.init(BRANCH_KEY, branchOptions);

  // Set user identity for cross-device attribution
  // https://help.branch.io/developers-hub/docs/web-full-reference#setidentityidentity-callback
  branch.setIdentity(deviceId);
}

function _initializeGoogleAnalytics() {
  if (shouldDisableTracking()) return;

  ReactGA.initialize(GOOGLE_ANALYTICS_KEY, {
    debug: false,
    gaOptions: {},
  });
}

async function _initializeHotjar(appName) {
  const isProduction = ENV === 'master';
  const isValidApp = appName && ['www', 'studio'].includes(appName);

  if (shouldDisableTracking() || !isProduction || !isValidApp) return;

  try {
    const Hotjar = await import('@hotjar/browser');
    const { HOTJAR_WWW, HOTJAR_STUDIO } = await import('@pray/shared/config');

    const appConfig = appName === 'www' ? HOTJAR_WWW : HOTJAR_STUDIO;

    Hotjar.default.init(appConfig.SITE_ID, appConfig.VERSION);
  } catch (error) {
    logger.error(`Hotjar initialization failed for ${appName} app:`, error);
  }
}

/**
 * Initialize Fathom Analytics
 * https://usefathom.com/docs
 * Note: Fathom is privacy-first and doesn't use cookies, so no consent checks needed
 */
function _initializeFathom() {
  if (!FATHOM_SITE_ID) return;

  try {
    Fathom.load(FATHOM_SITE_ID, {
      // Include query parameters in page view tracking
      // This tracks URLs like /page?utm_source=twitter as unique page views
      includedDomains: ['www.pray.com', 'pray.com', 'localhost', 'local.pray.com', 'local-www.pray.com'],
      // Disable automatic page view tracking - we'll handle it manually in onRouteUpdate
      auto: false,
    });

    logger.info('Fathom Analytics initialized');
  } catch (error) {
    logger.error('Fathom Analytics initialization failed:', error);
  }
}

export default {
  initialize,
  initializeFacebookPixel,
};
